import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react';
import { typography } from '@xqmsg/ui-core';
import breakpoints from './foundations/breakpoints';
import colors from './foundations/colors';
import shadows from './foundations/shadows';
import buttonPalettes from './buttonPalettes';
import accordion from './components/accordion';
import badge from './components/badge';
import button from './components/button';
import checkbox from './components/checkbox';
import code from './components/code';
import dialog from './components/modal';
import field from './components/field';
import heading from './components/heading';
import input from './components/input';
import inputAddon from './components/input-addon';
import link from './components/link';
import menu from './components/menu';
import nativeSelect from './components/native-select';
import switchRecipe from './components/switch';
import textarea from './components/textarea';
import styles from './styles';

/**
 * Wrap leaf values in the `{ value }` shape Chakra v3 tokens require; the
 * foundation files keep their flat v2 shape (they are imported directly
 * elsewhere in the app).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const toTokens = (obj: Record<string, any>): Record<string, any> =>
  Object.fromEntries(
    Object.entries(obj).map(([key, val]) => [
      key,
      val !== null && typeof val === 'object' ? toTokens(val) : { value: val },
    ])
  );

export const portalConfig = defineConfig({
  // No cssVarsRoot: v3 applies it to base tokens only, which breaks the whole
  // semantic layer. See changelog/chakra-v3-migration-log.md (`cssVarsRoot`).

  // v2 `styles.global` moved to top-level `globalCss`
  globalCss: styles.global,
  theme: {
    breakpoints,
    tokens: {
      // v2's border names, which v3 renamed to xs/sm/md/lg/xl. Unresolved,
      // `borderTop="1px"` reaches CSS as the `border-top: 1px` shorthand and
      // resets border-style to `none`, drawing nothing. Literals rather than
      // aliases onto v3's keys — those encode a scale step, not a width.
      borders: {
        none: { value: 'none' },
        '1px': { value: '1px solid' },
        '2px': { value: '2px solid' },
        '4px': { value: '4px solid' },
        '8px': { value: '8px solid' },
      },
      colors: toTokens(colors),
      shadows: toTokens(shadows),
      fonts: toTokens(typography.fonts),
      fontSizes: toTokens(typography.fontSizes),
      fontWeights: toTokens(typography.fontWeights),
      letterSpacings: toTokens(typography.letterSpacings),
      lineHeights: toTokens(typography.lineHeights),
    },
    // per-palette Button colours (v2 branched on colorScheme in JS)
    semanticTokens: {
      colors: {
        ...buttonPalettes,
      },
    },
    recipes: {
      badge,
      button,
      code,
      heading,
      input,
      inputAddon,
      link,
      textarea,
    },
    slotRecipes: {
      accordion,
      checkbox,
      dialog,
      field,
      menu,
      nativeSelect,
      switch: switchRecipe,
    },
  },
});

const customChakraTheme = createSystem(defaultConfig, portalConfig);

export type CustomTheme = typeof customChakraTheme;

export default customChakraTheme;
